import { useState, useEffect } from "react";
import { supabase, getStorageUrl } from "@/lib/supabase";
import { Image as ImageIcon, Upload, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/auth";

export default function HeroPosterTab() {
  const [posters, setPosters] = useState([]);
  const [busy, setBusy] = useState(false);
  const [discountText, setDiscountText] = useState("50% OFF<br/>SALE");
  const [savingText, setSavingText] = useState(false);

  const load = async () => {
    try {
      // Load posters
      const { data, error } = await supabase.storage.from("images").list("posters", {
        limit: 50,
        sortBy: { column: "created_at", order: "desc" },
      });
      if (error) throw error;
      
      const list = (data || [])
        .filter(f => f.name !== ".emptyFolderPlaceholder")
        .map(f => ({
          name: f.name,
          url: getStorageUrl(`posters/${f.name}`)
        }));
      setPosters(list);
    } catch (e) {
      console.error("Failed to load posters:", e);
    }

    try {
      // Load discount text
      const { data: textData, error: textError } = await supabase.storage.from("images").download("settings/hero-sticker.json");
      if (!textError) {
        const text = await textData.text();
        const json = JSON.parse(text);
        if (json.discountText) setDiscountText(json.discountText);
      }
    } catch (e) {
      console.error("Failed to load discount text:", e);
    }
  };

  useEffect(() => { load(); }, []);

  const saveText = async () => {
    setSavingText(true);
    try {
      const fileContent = JSON.stringify({ discountText });
      const file = new Blob([fileContent], { type: "application/json" });
      const { error } = await supabase.storage.from("images").upload("settings/hero-sticker.json", file, {
        upsert: true,
        cacheControl: "0"
      });
      if (error) throw error;
      toast.success("Discount text saved!");
    } catch (e) {
      toast.error(formatApiError(e) || e.message);
    } finally {
      setSavingText(false);
    }
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true);
    try {
      await Promise.all(files.map(async (file) => {
        const ext = file.name.split(".").pop();
        const fileName = `posters/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${ext}`;
        const { error } = await supabase.storage.from("images").upload(fileName, file);
        if (error) throw error;
      }));
      toast.success(`${files.length} poster(s) uploaded!`);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (name) => {
    if (!window.confirm("Delete this poster?")) return;
    setBusy(true);
    try {
      const { error } = await supabase.storage.from("images").remove([`posters/${name}`]);
      if (error) throw error;
      toast.success("Poster removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-hero-poster-tab">
      <div className="mb-8">
        <h2 className="font-display font-black uppercase text-3xl md:text-4xl">Hero Posters</h2>
        <p className="text-black/70">Manage the hero section slider and stickers on the homepage.</p>
      </div>

      <div className="nb-card p-6 bg-white mb-8">
        <h3 className="font-display font-black uppercase text-xl mb-4">Discount Sticker Text</h3>
        <div className="flex gap-3">
          <input 
            type="text" 
            value={discountText}
            onChange={(e) => setDiscountText(e.target.value)}
            placeholder="e.g. 50% OFF<br/>SALE" 
            className="flex-1 px-4 py-2 border-[3px] border-black rounded-lg shadow-[3px_3px_0_0_#0A0A0A] outline-none focus:shadow-[4px_4px_0_0_#FF3366] transition-all"
          />
          <button 
            onClick={saveText}
            disabled={savingText || !discountText.trim()}
            className="nb-btn bg-[var(--primary)] text-white disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Save size={18} strokeWidth={3} />
            {savingText ? "Saving..." : "Save Text"}
          </button>
        </div>
        <p className="text-sm text-black/50 mt-2">Use &lt;br/&gt; for line breaks. e.g. "50% OFF&lt;br/&gt;SALE"</p>
      </div>

      <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h3 className="font-display font-black uppercase text-xl">Poster Slider</h3>
          <p className="text-black/70 text-sm">Upload multiple posters to create an auto-scrolling slider.</p>
        </div>
        <label className="nb-btn bg-[var(--secondary)] text-black cursor-pointer inline-flex items-center gap-2">
          <Upload size={18} strokeWidth={3} />
          {busy ? "Uploading..." : "Upload Posters"}
          <input type="file" accept="image/*" multiple onChange={upload} className="hidden" disabled={busy} />
        </label>
      </div>

      {posters.length === 0 ? (
        <div className="nb-card p-12 text-center bg-white/50">
          <ImageIcon size={48} strokeWidth={2} className="mx-auto mb-3 opacity-20" />
          <p className="font-bold text-black/40 text-xl uppercase tracking-tight">No posters uploaded yet</p>
          <p className="text-black/30">The homepage will show the default image.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {posters.map((p) => (
            <div key={p.name} className="relative group">
              <div className="aspect-[4/5] rounded-[24px] nb-border overflow-hidden bg-[var(--muted)] nb-shadow transition-transform group-hover:-translate-y-1">
                <img src={p.url} alt="Poster" className="w-full h-full object-contain" />
              </div>
              <button
                onClick={() => remove(p.name)}
                disabled={busy}
                className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-black text-white flex items-center justify-center nb-border shadow-md hover:bg-red-500 transition-colors"
                title="Remove"
              >
                <Trash2 size={16} strokeWidth={3} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
